// frontend/src/components/PhraseManager/PhraseImport.js
/**
 * Import av fraslexikon från CSV (t.ex. export ur TakeCare).
 * Kolumn A = triggerord, kolumn B = text. Tre steg: välj fil -> förhandsvisning -> klart.
 * Förhandsvisningen skriver ingenting; servern tolkar filen på nytt vid import.
 */
import React, { useRef, useState } from 'react';
import { previewPhraseImport, importPhrases } from '../JournalEditor/api/journalApi';

// "1 fras" / "3 fraser"
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const STATUS_LABELS = {
  new: 'Ny',
  duplicate: 'Finns redan',
  unchanged: 'Oförändrad'
};

function PhraseImport({ onDone, onCancel }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showInvalid, setShowInvalid] = useState(false);
  // Bara svaret för senast vald fil får visas (byter man fil snabbt kan svaren komma i fel ordning)
  const latestRequest = useRef(0);

  const handleFile = async (selected) => {
    if (!selected) return;
    const requestId = ++latestRequest.current;
    setFile(selected);
    setPreview(null);
    setResult(null);
    setError(null);
    setBusy(true);
    try {
      const data = await previewPhraseImport(selected);
      if (requestId === latestRequest.current) setPreview(data);
    } catch (err) {
      if (requestId === latestRequest.current) {
        setError(err.response?.data?.error || 'Kunde inte läsa filen');
      }
    } finally {
      if (requestId === latestRequest.current) setBusy(false);
    }
  };

  const handleImport = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await importPhrases(file, onDuplicate));
    } catch (err) {
      setError(err.response?.data?.error || 'Importen misslyckades');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    latestRequest.current += 1;
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Klart
  if (result) {
    return (
      <div className="phrase-import">
        <div className="phrase-import-result">
          <h3>Importen är klar</h3>
          <ul>
            <li>{plural(result.created, 'ny fras', 'nya fraser')}</li>
            {result.updated > 0 && <li>{plural(result.updated, 'fras uppdaterad', 'fraser uppdaterade')}</li>}
            {result.skipped > 0 && <li>{plural(result.skipped, 'befintlig fras behölls', 'befintliga fraser behölls')}</li>}
            {result.unchanged > 0 && <li>{plural(result.unchanged, 'fras var redan identisk', 'fraser var redan identiska')}</li>}
            {result.invalid > 0 && <li>{plural(result.invalid, 'rad hoppades över (fel)', 'rader hoppades över (fel)')}</li>}
          </ul>
          <p>Du har nu {plural(result.count, 'fras', 'fraser')} i ditt fraslexikon.</p>
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={reset}>
              Importera en fil till
            </button>
            <button type="button" className="save-btn" onClick={onDone}>
              Visa fraslexikon
            </button>
          </div>
        </div>
      </div>
    );
  }

  const counts = preview?.counts || {};
  const invalid = preview?.invalid || [];

  return (
    <div className="phrase-import">
      {/* Steg 1: välj fil */}
      <div
        className="phrase-import-dropzone"
        role="button"
        tabIndex={0}
        aria-label="Välj CSV-fil att importera"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0]);
        }}
      >
        <div>
          <strong>{file ? file.name : 'Välj eller släpp en CSV-fil'}</strong>
          <span>
            Exportera fraslexikonet ur TakeCare som CSV. Kolumn A = triggerord,
            kolumn B = text. Avgränsare och teckenkodning känns igen automatiskt.
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,.tsv,text/csv,text/plain"
          onChange={e => {
            handleFile(e.target.files?.[0]);
            e.target.value = ''; // så att samma fil kan väljas igen (t.ex. efter ett fel)
          }}
          hidden
        />
      </div>

      {busy && !preview && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          Läser filen...
        </div>
      )}

      {error && (
        <div className="phrase-import-error">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {/* Steg 2: förhandsvisning */}
      {preview && (
        <>
          <div className="phrase-import-summary">
            <div className="summary-stat new">
              <strong>{counts.new || 0}</strong> nya
            </div>
            <div className="summary-stat duplicate">
              <strong>{counts.duplicate || 0}</strong> finns redan
            </div>
            <div className="summary-stat unchanged">
              <strong>{counts.unchanged || 0}</strong> oförändrade
            </div>
            <div className="summary-stat invalid">
              <strong>{invalid.length}</strong> fel
            </div>
          </div>
          <p className="phrase-import-meta">
            {plural(preview.total, 'fras hittades', 'fraser hittades')} ({preview.encoding}, avgränsare: {preview.delimiter}
            {preview.header_skipped ? ', rubrikrad ignorerad' : ''}
            {preview.extra_columns_ignored ? ', bara kolumn A och B används' : ''}
            {preview.empty_rows ? `, ${plural(preview.empty_rows, 'tom rad', 'tomma rader')} ignorerade` : ''}).
          </p>

          {counts.duplicate > 0 && (
            <div className="phrase-import-duplicates">
              <span>{counts.duplicate} triggerord finns redan i ditt lexikon med annan text:</span>
              <label>
                <input
                  type="radio"
                  name="on_duplicate"
                  value="skip"
                  checked={onDuplicate === 'skip'}
                  onChange={() => setOnDuplicate('skip')}
                />
                Behåll mina befintliga
              </label>
              <label>
                <input
                  type="radio"
                  name="on_duplicate"
                  value="overwrite"
                  checked={onDuplicate === 'overwrite'}
                  onChange={() => setOnDuplicate('overwrite')}
                />
                Skriv över med filens text
              </label>
            </div>
          )}

          {invalid.length > 0 && (
            <div className="phrase-import-invalid">
              <button type="button" className="link-btn" onClick={() => setShowInvalid(v => !v)}>
                <i className={`fas fa-chevron-${showInvalid ? 'down' : 'right'}`}></i>
                {plural(invalid.length, 'rad importeras inte', 'rader importeras inte')}
              </button>
              {showInvalid && (
                <ul>
                  {invalid.map(row => (
                    <li key={row.line}>
                      Rad {row.line}{row.title ? ` (;${row.title})` : ''}: {row.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="phrase-import-table-wrapper">
            <table className="phrase-table">
              <thead>
                <tr>
                  <th>Triggerord</th>
                  <th>Text</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(row => (
                  <tr key={row.line}>
                    <td className="phrase-title-cell"><code>;{row.title}</code></td>
                    <td className="phrase-text-cell">
                      <div className="phrase-text-preview">{row.phrase_text}</div>
                    </td>
                    <td>
                      <span className={`import-status ${row.status}`}>
                        {STATUS_LABELS[row.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="form-actions phrase-import-actions">
        <button type="button" className="cancel-btn" onClick={onCancel}>
          Avbryt
        </button>
        <button
          type="button"
          className="save-btn"
          onClick={handleImport}
          disabled={!preview || busy || (counts.new || 0) + (onDuplicate === 'overwrite' ? counts.duplicate || 0 : 0) === 0}
        >
          {busy && preview ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Importerar...
            </>
          ) : (
            'Importera'
          )}
        </button>
      </div>
    </div>
  );
}

export default PhraseImport;
